import {
  Card as AntdCard,
  Badge,
  Button,
  Col,
  Collapse,
  Descriptions,
  Row,
  Table,
  TablePaginationConfig,
  Tag,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import React from "react";
import { Link, NavLink } from "react-router-dom";
import {
  AssociatedAddress,
  AssociatedEmail,
  AssociatedPhone,
  Company,
  CompanyContact,
  Customer,
  CustomerCompanyAssociationContactLevel,
  Director,
  FileUpload,
  PaginatedResourcePageSelected,
  TemplateRecipientContactLevel,
} from "../helpers/shared-interfaces";
import { checkPermission, getStateColor, injectKey, request } from "../helpers/utilities";
import { CompanyContactPerson, CompanyDirector } from "../pages/BusinessDevelopment/CompanyDetails";
import CardSection from "./CardSection";
import Search from "antd/lib/input/Search";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import {
  SEARCH_CURRENT_EMPLOYEE,
  SEARCH_FORMER_EMPLOYEE,
} from "../gql/companies";
import { UploadedDocumentCard } from "./CompanyOnboardingDetails";
import { DEBOUNCE_TIME, tabPaneStyle } from "../helpers/constants";
import EntitledSection from "./EntitledSection";
import entitlements from "../config/entitlements";
import ModalCreator from "./ModalCreator";


const { Panel } = Collapse;

interface Props {
  editEntitlement?: string;
  emails: AssociatedEmail[];
  phones: AssociatedPhone[];
  adresses: AssociatedAddress[];
  style?: React.CSSProperties;
  onAddEmail?: () => void;
  onAddPhone?: () => void;
  onAddAddress?: () => void;
  onAddBackgroundInfo?: () => void;
  onEditBackgroundInfo?: () => void;
  onDeleteBackgroundInfo?: () => void;
  onMakePrimaryEmail?: (email: AssociatedEmail) => void;
  onMakePrimaryPhone?: (phone: AssociatedPhone) => void;
  onMakePrimaryAddress?: (address: AssociatedAddress) => void;
  onEditEmail?: (email: AssociatedEmail) => void;
  onDeleteEmail?: (email: AssociatedEmail) => void;
  onReactivateEmail?: (email: AssociatedEmail) => void;
  onEditPhone?: (phone: AssociatedPhone) => void;
  onDeletePhone?: (phone: AssociatedPhone) => void;
  onReactivatePhone?: (phone: AssociatedPhone) => void;
  onEditAddress?: (address: AssociatedAddress) => void;
  onDeleteAddress?: (address: AssociatedAddress) => void;
  onReactivateAddress?: (address: AssociatedAddress) => void;
  formerEmployees: { data: Customer[]; totalCount: number };
  currentEmployees: { data: Customer[]; totalCount: number };
  currentEmployeesPage?: number;
  formerEmployeesPage?: number;
  paginatedResourceNextPage?: (
    page: number,
    paginatedResource: PaginatedResourcePageSelected,
    numberPerPage: number,
  ) => void;
  companyContacts?: CompanyContact[];
  companyPetraId?: string;
  numberPerPage?: number;
  directors?: Director[]
  companyInfoFileUploads?: FileUpload[]
  customerInfoFileUploads?: FileUpload[]
  customer?: Customer
  onAddCompanyContact?: () => void;
  onUpload?: () => void;
  onEditCompanyContact?: (contact: CompanyContact) => void;
  onDeleteCompanyContact?: (contact: CompanyContact) => void;
  onAddCompanyDirector?: () => void;
  onEditCompanyDirector?: (director: Director) => void;
  onDeleteCompanyDirector?: (director: Director) => void;
  onAddDirectorContact?: (director: Director) => void;
  onDeleteDirectorContact?: (contact: string, directorId: string) => void;
  onUploadCompanyInfoFile?: () => void;
  onDeleteCompanyInfoFile?: (id?: string) => void;
  onSetEntityToBeDeleted?: (entity: string) => void;
  onUploadCustomerInfoFile?: () => void;
  onDeleteCustomerInfoFile?: (id?: string) => void;
}

const actionRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 12,
};

const cardActionStyle: React.CSSProperties = {
  display: "flex",
  gap: 8,
  marginTop: 10,
  flexWrap: "wrap",
  alignItems: "center",
};

const actionButtonStyle: React.CSSProperties = {
  borderStyle: "dashed",
};

const primaryEmailCardStyle: React.CSSProperties = {
  backgroundColor: "#f3fff1",
  borderColor: "#b7eb8f",
};

export const columns = [
  {
    title: "Name",
    dataIndex: "name",
    key: "firstName",
    width: "50%",
    render(text: string, record: Customer): JSX.Element {
      return (
        <span>
          {record.firstName} {record.lastName}
        </span>
      );
    },
  },
  {
    title: "PetraId",
    dataIndex: "petraId",
    key: "petraId",
    width: "50%",
    render(text: string, record: Customer): JSX.Element {
      return <NavLink to={`/customers/${record.id}`}>{record.petraId}</NavLink>;
    },
  },
];

export function EmployeesTableFactory(
  data: Customer[],
  page: number | undefined,
  numberPerPage?: number,
  customerSearchQuery?: string,
  handleSearchInput?: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>,
  onNextPage?: (
    page: number,
    paginatedResource: PaginatedResourcePageSelected,
    numberPerPage: number,
  ) => void,
  paginatedResource?: PaginatedResourcePageSelected,
  totalCount?: number,
): JSX.Element {
  let paginationInformation: TablePaginationConfig = {
    defaultPageSize: numberPerPage,
    total: totalCount ?? data.length,
    current: 1,
  };

  if (onNextPage !== undefined && paginatedResource !== undefined) {
    paginationInformation = {
      ...paginationInformation,
      current: page,
      onChange: (page, numberPerPage) =>
        onNextPage(page, paginatedResource, numberPerPage),
    };
  }

  return (
    <>
      <Search
        placeholder="Search by Name, PetraId"
        style={{ width: "100%", marginTop: "10px" }}
        onChange={handleSearchInput}
        value={customerSearchQuery}
        data-testid="filter-input"
        allowClear
        id="search-input-customer"
      />
      <Table
        bordered={true}
        columns={columns}
        dataSource={injectKey<Customer>(data, "id")}
        pagination={paginationInformation}
      />
    </>
  );
}

export const EmailCard = (props: { record: AssociatedEmail }): JSX.Element => {
  return (
    <div>
      <CardSection
        headingText="Email"
        data={
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span>{props.record.email.address}</span>
            {!props.record.isActive && (
              <Tag
                color={getStateColor("inactive")}
                style={{
                  marginBottom: "2px",
                }}
              >
                Inactive
              </Tag>
            )}
          </span>
        }
      />
    </div>
  );
};

export const PhoneCard = (props: { record: AssociatedPhone }): JSX.Element => {
  return (
    <div>
      <CardSection
        headingText="Number"
        data={
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span>{props.record.phone.number}</span>           
            {!props.record.isActive && (
              <Tag
                color={getStateColor("inactive")}
                style={{
                  marginBottom: "2px",
                }}
              >
                Inactive
              </Tag>
            )}
          </span>
        }
      />
    </div>
  );
};

export const AddressCard = (props: {
  record: AssociatedAddress;
}): JSX.Element => {
  if (!props.record.address) return <></>;

  return (
    <div>
      {props.record.address.streetName && (
        <div style={{ marginBottom: "5px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <span
              style={{
                fontWeight: "bold",
                fontSize: "12px",
              }}
            >
              Street Name
            </span>        
            {!props.record.isActive && (
              <Tag
                color={getStateColor("inactive")}
                style={{
                  marginBottom: "2px",
                }}
              >
                Inactive
              </Tag>
            )}
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "#333",
              marginTop: 2,
            }}
          >
            {props.record.address.streetName}
          </div>
        </div>
      )}

    
        <CardSection
          headingText="Box Address"
          data={props.record.address.poBox ?? "N/A"}
        />
     

        <CardSection
          headingText=" Digital Address (GhanaPostGPS Code)"
          data={String(props.record.address.ghPost ?? "N/A")}
        />
    
     
        <CardSection
          headingText="Country"
          data={props.record.address.country.name ?? "N/A"}
        />
  

      
        <CardSection
          headingText="Region"
          data={String(props.record.address.region) ?? "N/A"}
        />
    
        <CardSection headingText="City" data={props.record.address.city ?? "N/A"} />
 

    </div>
  );
};

export const FormerEmployeesCard = (props: {
  formerEmployee: Customer;
}): JSX.Element => {
  return (
    <div>
      <CardSection
        headingText="Name"
        data={
          <Link to={`/customers/${props.formerEmployee.id}`}>
            {props.formerEmployee.firstName} {props.formerEmployee.lastName}
          </Link>
        }
      />
      <CardSection headingText="PetraId" data={props.formerEmployee.petraId} />
    </div>
  );
};

export const CurrentEmployeesCard = (props: {
  currentEmployee: Customer;
}): JSX.Element => {
  return (
    <div>
      <CardSection
        headingText="Name"
        data={
          <Link to={`/customers/${props.currentEmployee.id}`}>
            {props.currentEmployee.firstName} {props.currentEmployee.lastName}
          </Link>
        }
      />
      <CardSection headingText="PetraId" data={props.currentEmployee.petraId} />
    </div>
  );
};

export const mapContactLevels = (
  levels:
    | TemplateRecipientContactLevel[]
    | CustomerCompanyAssociationContactLevel[],
): JSX.Element => {
  return (
    <div style={{ marginLeft: "5px" }}>
      {levels.map(
        (
          level:
            | TemplateRecipientContactLevel
            | CustomerCompanyAssociationContactLevel,
          index,
        ) => (
          <Tag
            key={index}
            id={`template-tag-${index}`}
            style={{ paddingRight: "4px" }}
          >
            {level.contactLevel.name}
          </Tag>
        ),
      )}
    </div>
  );
};

interface State {
  customerSearchQuery: string;
  currentEmployees: Customer[];
  formerEmployees: Customer[];
  currentEmployeesSearchQuery: string;
  formerEmployeesSearchQuery: string;
  showMakePrimaryModal: boolean;
  primaryActionLoading: boolean;
  pendingPrimaryType: "email" | "phone" | "address" | null;
  pendingPrimaryRecord:
    | AssociatedEmail
    | AssociatedPhone
    | AssociatedAddress
    | null;
}

export default class ContactDetailsView extends React.Component<Props, State> {
  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      customerSearchQuery: "",
      currentEmployees: [],
      formerEmployees: [],
      currentEmployeesSearchQuery: "",
      formerEmployeesSearchQuery: "",
      showMakePrimaryModal: false,
      primaryActionLoading: false,
      pendingPrimaryType: null,
      pendingPrimaryRecord: null,
    };
  }

  openMakePrimaryModal = (
    type: "email" | "phone" | "address",
    record: AssociatedEmail | AssociatedPhone | AssociatedAddress,
  ): void => {
    this.setState({
      showMakePrimaryModal: true,
      pendingPrimaryType: type,
      pendingPrimaryRecord: record,
    });
  };

  closeMakePrimaryModal = (): void => {
    this.setState({
      showMakePrimaryModal: false,
      primaryActionLoading: false,
      pendingPrimaryType: null,
      pendingPrimaryRecord: null,
    });
  };

  confirmMakePrimary = async (): Promise<void> => {
    const { pendingPrimaryType, pendingPrimaryRecord } = this.state;
    if (!pendingPrimaryType || !pendingPrimaryRecord) return;

    this.setState({ primaryActionLoading: true });

    try {
      if (pendingPrimaryType === "email" && this.props.onMakePrimaryEmail) {
        await this.props.onMakePrimaryEmail(
          pendingPrimaryRecord as AssociatedEmail,
        );
      }

      if (pendingPrimaryType === "phone" && this.props.onMakePrimaryPhone) {
        await this.props.onMakePrimaryPhone(
          pendingPrimaryRecord as AssociatedPhone,
        );
      }

      if (
        pendingPrimaryType === "address" &&
        this.props.onMakePrimaryAddress
      ) {
        await this.props.onMakePrimaryAddress(
          pendingPrimaryRecord as AssociatedAddress,
        );
      }
    } finally {
      this.closeMakePrimaryModal();
    }
  };

  getPrimaryEntityLabel = (): string => {
    const { pendingPrimaryType } = this.state;
    if (pendingPrimaryType === "phone") return "number";
    if (pendingPrimaryType === "address") return "address";
    return "email";
  };

  componentDidMount(): void {
    this.updateEmployeesState();
  }

  componentDidUpdate(prevProps: Props): void {
    if (
      prevProps.currentEmployees !== this.props.currentEmployees ||
      prevProps.formerEmployees !== this.props.formerEmployees
    ) {
      this.updateEmployeesState();
    }
  }

  updateEmployeesState(): void {
    const { currentEmployees, formerEmployees } = this.props;

    if (
      currentEmployees.data !== this.state.currentEmployees ||
      formerEmployees.data !== this.state.formerEmployees
    ) {
      this.setState({
        currentEmployees: currentEmployees.data || [],
        formerEmployees: formerEmployees.data || [],
      });
    }
  }

  handleSearchInput = async (
    event: React.ChangeEvent<HTMLInputElement>,
    isCurrentEmployees: boolean,
  ): Promise<void> => {
    const searchQueryKey = isCurrentEmployees
      ? "currentEmployeesSearchQuery"
      : "formerEmployeesSearchQuery";

    const newState = {
      [searchQueryKey]: event.target.value,
    };

    if (event.target.value.length > 0) {
      this.setState(
        (prevState) => ({
          ...prevState,
          ...newState,
        }),
        () => this.triggerDelayedSearch(isCurrentEmployees),
      );
    } else {
      this.setState({
        ...newState,
        currentEmployees: this.props.currentEmployees.data,
        formerEmployees: this.props.formerEmployees.data,
      });
    }
  };

  triggerDelayedSearch = debounce(
    async (isCurrentEmployees: boolean): Promise<void> => {
      return await this.triggerImmediateSearch(isCurrentEmployees);
    },
    DEBOUNCE_TIME,
  );

  triggerImmediateSearch = (isCurrentEmployees: boolean): void => {
    const searchQueryKey = isCurrentEmployees
      ? "currentEmployeesSearchQuery"
      : "formerEmployeesSearchQuery";

    const customerSearchQuery = this.state[searchQueryKey];

    interface Response {
      data: {
        findCompanyByPetraId: Company;
      };
    }

    if (isCurrentEmployees) {
      request({
        query: SEARCH_CURRENT_EMPLOYEE,
        variables: {
          petraId: this.props.companyPetraId,
          associationNameTwo: "current_employees",
          customerPageTwo: 1,
          q: customerSearchQuery,
          numberPerPage: 25,
        },
      }).then((response: AxiosResponse<Response>): void => {
        const currentEmployeesResult =
          response.data.data?.findCompanyByPetraId?.currentEmployees?.results ||
          [];

        this.setState({
          currentEmployees: currentEmployeesResult as Customer[],
        });
      });
    } else {
      request({
        query: SEARCH_FORMER_EMPLOYEE,
        variables: {
          petraId: this.props.companyPetraId,
          associationNameOne: "former_employees",
          customerPageOne: 1,
          q: customerSearchQuery,
          numberPerPage: 25,
        },
      }).then((response: AxiosResponse<Response>): void => {
        const formerEmployeesResult =
          response.data.data?.findCompanyByPetraId?.formerEmployees?.results ||
          [];
        this.setState({
          formerEmployees: formerEmployeesResult as Customer[],
        });
      });
    }
  };

  render(): JSX.Element {
    const backgroundInfo = this.props.customer?.customerBackgroundInformation;
    const hasBackgroundInfo =
      !!backgroundInfo &&
      Object.values(backgroundInfo).some(
        (value) => value !== undefined && value !== null && value !== "",
      );
    const stringifiedUser = localStorage.getItem("user");
    const user = stringifiedUser ? JSON.parse(stringifiedUser) : null;
    const canEditCustomers = user
      ? checkPermission(user, entitlements.Customers.edit).canAccess
      : false;
    const canEditCompanies = user
      ? checkPermission(user, entitlements.Companies.edit).canAccess
      : false;
    const editEntitlement =
      this.props.editEntitlement || entitlements.Customers.edit;

    return (
      <div
        style={{
          border: "1px solid rgb(232 232 232)",
          padding: "20px",
          boxSizing: "border-box",
          ...this.props.style,
        }}
      >
        <Descriptions title={<div>Contact Details </div>}></Descriptions>
        {ModalCreator({
          title: "Confirm Action",
          okType: "primary",
          okText: "Make Primary",
          showModal: this.state.showMakePrimaryModal,
          onOkay: this.confirmMakePrimary,
          toggleModal: this.closeMakePrimaryModal,
          isLoading: this.state.primaryActionLoading,
          inscription: `Are you sure you want to make this ${this.getPrimaryEntityLabel()} primary?`,
        })}
        <Collapse>
          <Panel
            header={
              <span>
                Emails{" "}
                <Badge
                  count={this.props.emails?.length ?? 0}
                  style={tabPaneStyle}
                />
              </span>
            }
            key="Emails"
          >
            <div style={actionRowStyle}>
              <EntitledSection entitlement={editEntitlement}>
                {this.props.onAddEmail && (
                  <Button
                    className="base-button"
                    onClick={this.props.onAddEmail}
                    icon={<PlusOutlined />}
                  >
                    Add Email
                  </Button>
                )}
              </EntitledSection>
            </div>
            <Row gutter={[16, 16]}>
              {this.props?.emails?.map(
                (customerEmail: AssociatedEmail): JSX.Element => {
                  return (
                    <Col key={customerEmail.id} span={7}>
                      <AntdCard
                        className="associated-entity-card"
                        key={customerEmail.id}
                        style={
                          customerEmail.isPrimary
                            ? primaryEmailCardStyle
                            : undefined
                        }
                      >
                        {EmailCard({ record: customerEmail })}
                        <div style={cardActionStyle}>
                          {!customerEmail.isActive ? (
                            <EntitledSection
                              entitlement={editEntitlement}
                            >
                              {this.props.onReactivateEmail && (
                                <Button
                                  onClick={() =>
                                    this.props.onReactivateEmail?.(customerEmail)
                                  }
                                  className="success-btn"
                                >
                                  Reactivate
                                </Button>
                              )}
                            </EntitledSection>
                          ) : (
                            <>
                              {!customerEmail.isPrimary && (
                                <EntitledSection
                                  entitlement={editEntitlement}
                                >
                                  {this.props.onMakePrimaryEmail && (
                                    <Button
                                      type="primary"
                                      onClick={() =>
                                        this.openMakePrimaryModal(
                                          "email",
                                          customerEmail,
                                        )
                                      }
                                    >
                                      Make Primary
                                    </Button>
                                  )}
                                </EntitledSection>
                              )}
                              <EntitledSection
                                entitlement={editEntitlement}
                              >
                                {this.props.onEditEmail && (
                                  <Button
                                    icon={<EditOutlined />}
                                    onClick={() =>
                                      this.props.onEditEmail?.(customerEmail)
                                    }
                                    style={actionButtonStyle}
                                  >                                
                                  </Button>
                                )}
                              </EntitledSection>
                              {!customerEmail.isPrimary && (
                                <EntitledSection
                                  entitlement={editEntitlement}
                                >
                                  {this.props.onDeleteEmail && (
                                    <Button
                                      icon={<DeleteOutlined />}
                                      onClick={() =>
                                        this.props.onDeleteEmail?.(customerEmail)
                                      }
                                      danger
                                    >                                 
                                    </Button>
                                  )}
                                </EntitledSection>
                              )}
                            </>
                          )}
                        </div>
                      </AntdCard>
                    </Col>
                  );
                },
              )}
            </Row>
          </Panel>
          <Panel
            header={
              <span>
                Phones{" "}
                <Badge
                  count={this.props.phones?.length ?? 0}
                  style={tabPaneStyle}
                />
              </span>
            }
            key="Phones"
          >
            <div style={actionRowStyle}>
              <EntitledSection entitlement={editEntitlement}>
                {this.props.onAddPhone && (
                  <Button
                    className="base-button"
                    onClick={this.props.onAddPhone}
                    icon={<PlusOutlined />}
                  >
                    Add Phone
                  </Button>
                )}
              </EntitledSection>
            </div>
            <Row gutter={[16, 16]}>
              {(this.props.phones ?? []).map(
                (customerPhone: AssociatedPhone): JSX.Element => {
                  return (
                    <Col key={customerPhone.id} span={7}>
                      <AntdCard
                        className="associated-entity-card"
                        key={customerPhone.id}
                         style={
                          customerPhone.isPrimary
                            ? primaryEmailCardStyle
                            : undefined
                        }
                      >
                        {PhoneCard({ record: customerPhone })}
                        <div style={cardActionStyle}>
                          {!customerPhone.isActive ? (
                            <EntitledSection
                              entitlement={editEntitlement}
                            >
                              {this.props.onReactivatePhone && (
                                <Button
                                  onClick={() =>
                                    this.props.onReactivatePhone?.(customerPhone)
                                  }
                                  className="success-btn"
                                >
                                  Reactivate
                                </Button>
                              )}
                            </EntitledSection>
                          ) : (
                            <>
                              {!customerPhone.isPrimary && (
                                <EntitledSection
                                  entitlement={editEntitlement}
                                >
                                  {this.props.onMakePrimaryPhone && (
                                    <Button
                                      type="primary"
                                      onClick={() =>
                                        this.openMakePrimaryModal(
                                          "phone",
                                          customerPhone,
                                        )
                                      }
                                    >
                                      Make Primary
                                    </Button>
                                  )}
                                </EntitledSection>
                              )}
                              <EntitledSection
                                entitlement={editEntitlement}
                              >
                                {this.props.onEditPhone && (
                                  <Button
                                    icon={<EditOutlined />}
                                    onClick={() =>
                                      this.props.onEditPhone?.(customerPhone)
                                    }
                                    style={actionButtonStyle}
                                  >                             
                                  </Button>
                                )}
                              </EntitledSection>
                              {!customerPhone.isPrimary && (
                                <EntitledSection
                                  entitlement={editEntitlement}
                                >
                                  {this.props.onDeletePhone && (
                                    <Button
                                      icon={<DeleteOutlined />}
                                      onClick={() =>
                                        this.props.onDeletePhone?.(customerPhone)
                                      }
                                      danger
                                    >                                
                                    </Button>
                                  )}
                                </EntitledSection>
                              )}
                            </>
                          )}
                        </div>
                      </AntdCard>
                    </Col>
                  );
                },
              )}
            </Row>
          </Panel>
          <Panel
            header={
              <span>
                Addresses{" "}
                <Badge
                  count={this.props.adresses?.length}
                  style={tabPaneStyle}
                />
              </span>
            }
            key="Addresses"
          >
            <div style={actionRowStyle}>
              <EntitledSection entitlement={editEntitlement}>
                {this.props.onAddAddress && (
                  <Button
                    className="base-button"
                    onClick={this.props.onAddAddress}
                    icon={<PlusOutlined />}
                  >
                    Add Address
                  </Button>
                )}
              </EntitledSection>
            </div>
            <Row gutter={[16, 16]}>
              {this.props.adresses?.map(
                (customerAddress: AssociatedAddress): JSX.Element => {
                  return (
                    <Col key={customerAddress.id} span={7}>
                      <AntdCard
                        className="associated-entity-card"
                        key={customerAddress.id}
                         style={
                          customerAddress.isPrimary
                            ? primaryEmailCardStyle
                            : undefined
                        }
                      >
                        {AddressCard({ record: customerAddress })}
                        <div style={cardActionStyle}>
                          {!customerAddress.isActive ? (
                            <EntitledSection
                              entitlement={editEntitlement}
                            >
                              {this.props.onReactivateAddress && (
                                <Button
                                  onClick={() =>
                                    this.props.onReactivateAddress?.(
                                      customerAddress,
                                    )
                                  }
                                  className="success-btn"
                                >
                                  Reactivate
                                </Button>
                              )}
                            </EntitledSection>
                          ) : (
                            <>
                              {!customerAddress.isPrimary && (
                                <EntitledSection
                                  entitlement={editEntitlement}
                                >
                                  {this.props.onMakePrimaryAddress && (
                                    <Button
                                      type="primary"
                                      onClick={() =>
                                        this.openMakePrimaryModal(
                                          "address",
                                          customerAddress,
                                        )
                                      }
                                    >
                                      Make Primary
                                    </Button>
                                  )}
                                </EntitledSection>
                              )}
                              <EntitledSection
                                entitlement={editEntitlement}
                              >
                                {this.props.onEditAddress && (
                                  <Button
                                    icon={<EditOutlined />}
                                    onClick={() =>
                                      this.props.onEditAddress?.(customerAddress)
                                    }
                                    style={actionButtonStyle}
                                  >                                
                                  </Button>
                                )}
                              </EntitledSection>
                              {!customerAddress.isPrimary && (
                                <EntitledSection
                                  entitlement={editEntitlement}
                                >
                                  {this.props.onDeleteAddress && (
                                    <Button
                                      data-cy="test-user-delete-button"
                                      icon={<DeleteOutlined />}
                                      onClick={() =>
                                        this.props.onDeleteAddress?.(
                                          customerAddress,
                                        )
                                      }
                                      danger
                                    >
                                   
                                    </Button>
                                  )}
                                </EntitledSection>
                              )}
                            </>
                          )}
                        </div>
                      </AntdCard>
                    </Col>
                  );
                },
              )}
            </Row>
          </Panel>

          {this.props.customer && (
            <Panel
              header={
                <span>
                  Background Info{" "}
                  <Badge
                    count={hasBackgroundInfo ? 1 : 0}
                    style={tabPaneStyle}
                  />
                </span>
              }
              key="BackgroundInfo"
            >
              {!hasBackgroundInfo ? (
                <div style={actionRowStyle}>
                  <EntitledSection entitlement={entitlements.Customers.edit}>
                    {this.props.onAddBackgroundInfo && (
                      <Button
                        className="base-button"
                        onClick={this.props.onAddBackgroundInfo}
                        icon={<PlusOutlined />}
                      >
                        Add Background Info
                      </Button>
                    )}
                  </EntitledSection>
                </div>
              ) : (
                <AntdCard className="associated-entity-card" style={{ maxWidth: "500px"}}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 12,
                    }}
                  >
                    <span style={{ fontWeight: 600, flex: 1 }}>Background Info</span>
                    <EntitledSection entitlement={entitlements.Customers.edit}>
                      {this.props.onEditBackgroundInfo && (
                        <Button
                          onClick={this.props.onEditBackgroundInfo}
                          icon={<EditOutlined />}
                          style={{ ...actionButtonStyle, marginLeft: 0 }}
                        />
                      )}
                    </EntitledSection>
                  </div>
                  <Row>
                    <Col span={12}>
                      {this.props.customer?.customerBackgroundInformation?.fatherName && (
                        <CardSection
                          headingText="Father's Name"
                          data={this.props.customer.customerBackgroundInformation?.fatherName}
                        />
                      )}
                      {this.props.customer?.customerBackgroundInformation?.fatherDeceased !== undefined && (
                        <CardSection
                          headingText="Father Deceased"
                          data={this.props.customer.customerBackgroundInformation?.fatherDeceased ? "Yes" : "No"}
                        />
                      )}
                      {this.props.customer?.customerBackgroundInformation?.fatherPhoneNumber && (
                        <CardSection
                          headingText="Father's Phone"
                          data={this.props.customer.customerBackgroundInformation?.fatherPhoneNumber || "N/A"}
                        />
                      )}
                      {this.props.customer?.customerBackgroundInformation?.motherName && (
                        <CardSection
                          headingText="Mother's Name"
                          data={this.props.customer.customerBackgroundInformation?.motherName}
                        />
                      )}
                      {this.props.customer?.customerBackgroundInformation?.motherDeceased !== undefined && (
                        <CardSection
                          headingText="Mother Deceased"
                          data={this.props.customer.customerBackgroundInformation?.motherDeceased ? "Yes" : "No"}
                        />
                      )}
                       {this.props.customer?.customerBackgroundInformation?.motherPhoneNumber && (
                        <CardSection
                          headingText="Mother's Phone"
                          data={this.props.customer.customerBackgroundInformation?.motherPhoneNumber || "N/A"}
                        />
                      )}
                    </Col>
                    <Col span={12}>
                      <CardSection
                        headingText="Previous SSNIT Number"
                        data={
                          this.props.customer?.customerBackgroundInformation?.previousSsnitNumber || "N/A"
                        }
                      />
                      <CardSection
                        headingText="Next Of Kin Name"
                        data={
                          this.props.customer?.customerBackgroundInformation?.nextOfKinName || "N/A"
                        }
                      />
                      <CardSection
                        headingText="Next Of Kin Phone"
                        data={
                          this.props.customer?.customerBackgroundInformation?.nextOfKinPhoneNumber || "N/A"
                        }
                      />
                      <CardSection
                        headingText="Previous Employer"
                        data={
                          this.props.customer?.customerBackgroundInformation?.previousEmployer || "N/A"
                        }
                      />
                      {this.props.customer?.customerBackgroundInformation?.hometown && (
                        <CardSection
                          headingText="Hometown"
                          data={this.props.customer.customerBackgroundInformation?.hometown}
                        />
                      )}
                    </Col>
                  </Row>
                </AntdCard>
              )}
            </Panel>
          )}

          {this.props.formerEmployees?.totalCount > 0 && (
            <Panel
              header={
                <span>
                  Former Employees{" "}
                  <Badge
                    count={this.props.formerEmployees?.totalCount}
                    style={tabPaneStyle}
                  />
                </span>
              }
              key="FormerEmployees"
            >
              {EmployeesTableFactory(
                this.state.formerEmployees,
                this.props.formerEmployeesPage,
                this.props.numberPerPage,
                this.state.formerEmployeesSearchQuery,
                (event) => this.handleSearchInput(event, false),
                this.props.paginatedResourceNextPage,
                PaginatedResourcePageSelected.formerEmployees,
                this.props.formerEmployees?.totalCount,
              )}
            </Panel>
          )}

          {this.props?.currentEmployees?.totalCount > 0 && (
            <Panel
              header={
                <span>
                  Current Employees{" "}
                  <Badge
                    count={this.props.currentEmployees?.totalCount ?? 0}
                    style={tabPaneStyle}
                  />
                </span>
              }
              key="currentEmployees"
            >
              {EmployeesTableFactory(
                this.state.currentEmployees,
                this.props.currentEmployeesPage,
                this.props.numberPerPage,
                this.state.currentEmployeesSearchQuery,
                (event) => this.handleSearchInput(event, true),
                this.props.paginatedResourceNextPage,
                PaginatedResourcePageSelected.currentEmployees,
                this.props.currentEmployees?.totalCount,
              )}
            </Panel>
          )}

          {this.props.companyContacts && (
            <Panel
              header={
                <span>
                  Company Contact Persons{" "}
                  <Badge
                    count={this.props.companyContacts?.length ?? 0}
                    style={tabPaneStyle}
                  />
                </span>
              }
              key="ContactPersons"
            >
              {this.props.onAddCompanyContact && (
                <div style={actionRowStyle}>
                  <EntitledSection entitlement={entitlements.Companies.edit}>
                    <Button
                      className="base-button"
                      onClick={() => this.props.onAddCompanyContact?.()}
                      icon={<PlusOutlined />}
                    >
                      Add Contact
                    </Button>
                  </EntitledSection>
                </div>
              )}
              <Row gutter={[16, 16]}>
                {(this.props.companyContacts ?? []).map(
                  (association: CompanyContact): JSX.Element => {
                    const editButtons = (
                      <div style={cardActionStyle}>
                        <EntitledSection
                          entitlement={entitlements.Companies.edit}
                        >
                          {this.props.onEditCompanyContact && (
                            <Button
                              icon={<EditOutlined />}
                              onClick={() =>
                                this.props.onEditCompanyContact?.(association)
                              }
                              style={actionButtonStyle}
                            >
                         
                            </Button>
                          )}
                        </EntitledSection>
                        <EntitledSection
                          entitlement={entitlements.Companies.edit}
                        >
                          {this.props.onDeleteCompanyContact && (
                            <Button
                              icon={<DeleteOutlined />}
                              onClick={() =>
                                this.props.onDeleteCompanyContact?.(association)
                              }
                              danger
                            >                         
                            </Button>
                          )}
                        </EntitledSection>
                      </div>
                    );
                    return (
                      <Col key={association.id} span={8}>
                        <AntdCard>
                          {CompanyContactPerson(association, {}, editButtons)}
                        </AntdCard>
                      </Col>
                    );
                  },
                )}
              </Row>
            </Panel>
          )}

          {this.props.directors && (
            <Panel
              header={
                <span>
                  Company Directors{" "}
                  <Badge
                    count={this.props.directors?.length ?? 0}
                    style={tabPaneStyle}
                  />
                </span>
              }
              key="Directors"
            >
               {this.props.onAddCompanyDirector && (
                <div style={actionRowStyle}>
                  <EntitledSection entitlement={entitlements.Companies.edit}>
                    <Button
                      className="base-button"
                      onClick={this.props.onAddCompanyDirector}
                      icon={<PlusOutlined />}
                    >
                      Add Director
                    </Button>
                  </EntitledSection>
                </div>
              )}
              <Row gutter={[16, 16]}>
                {(this.props.directors ?? []).map(
                  (director: Director): JSX.Element => {
                    const actionButtons = (
                      <div style={cardActionStyle}>
                        {this.props.onAddDirectorContact && (
                          <EntitledSection
                            entitlement={entitlements.Companies.edit}
                          >
                            <Button
                              icon={<EditOutlined />}
                              onClick={() =>
                                this.props.onAddDirectorContact?.(director)
                              }
                              className="base-button"
                            >
                              Add Contact
                            </Button>
                          </EntitledSection>
                        )}
                        <EntitledSection
                          entitlement={entitlements.Companies.edit}
                        >
                          {this.props.onDeleteCompanyDirector && (
                            <Button
                              icon={<DeleteOutlined />}
                              onClick={() =>
                                this.props.onDeleteCompanyDirector?.(director)
                              }                            
                              danger
                            >                       
                            </Button>
                          )}
                        </EntitledSection>
                      </div>
                    );
                    return (
                      <Col key={director.id} span={8}>
                        <AntdCard>
                          {CompanyDirector(
                            director,
                            {},
                            actionButtons,
                            canEditCompanies
                              ? this.props.onDeleteDirectorContact
                              : undefined,
                          )}
                        </AntdCard>
                      </Col>
                    );
                  },
                )}
              </Row>
            </Panel>
          )}

          {this.props?.companyInfoFileUploads && (
            <Panel
              header={
                <span>
                  Required Documents{" "}
                  <Badge
                    count={this.props.companyInfoFileUploads?.length ?? 0}
                    style={tabPaneStyle}
                  />
                </span>
              }
              key="Company Required Documents"
            >
               {this.props.onUploadCompanyInfoFile && (
                <div style={actionRowStyle}>
                  <EntitledSection entitlement={entitlements.Companies.edit}>
                    <Button
                      className="base-button"
                      onClick={this.props.onUploadCompanyInfoFile}
                      icon={<PlusOutlined />}
                    >
                      Add Document
                    </Button>
                  </EntitledSection>
                </div>
              )}
              <Row gutter={[16, 16]}>
                {(this.props.companyInfoFileUploads ?? []).map((file) => (
                   <Col key={file.id} xs={24} sm={12} md={12} lg={12} xl={12}>
                    <UploadedDocumentCard
                      document={file as FileUpload}
                      type={(file as FileUpload).fileType}
                      isEditing={canEditCompanies}
                      setEntityToBeDeleted={
                        canEditCompanies
                          ? this.props.onSetEntityToBeDeleted
                          : undefined
                      }
                      toggleDeleteConfirmationModal={
                        canEditCompanies
                          ? this.props.onDeleteCompanyInfoFile
                          : undefined
                      }
                    />
                  </Col>
                ))}
              </Row>
            </Panel>
          )}

          {this.props?.customerInfoFileUploads &&  (
            <Panel
              header={
                <span>
                  Required Documents{" "}
                  <Badge
                    count={this.props.customerInfoFileUploads?.length ?? 0}
                    style={tabPaneStyle}
                  />
                </span>
              }
              key="Customer Required Documents"
            >
               {this.props.onUploadCustomerInfoFile && (
                <div style={actionRowStyle}>
                  <EntitledSection entitlement={entitlements.Customers.edit}>
                    <Button
                      className="base-button"
                      onClick={this.props.onUploadCustomerInfoFile}
                      icon={<PlusOutlined />}
                    >
                      Add Document
                    </Button>
                  </EntitledSection>
                </div>
              )}
              <Row gutter={[16, 16]}>
                {(this.props.customerInfoFileUploads ?? []).map((file) => (
                  <Col key={file.id} xs={24} sm={12} md={12} lg={12} xl={12}>
                    <UploadedDocumentCard
                      document={file as FileUpload}
                      type={(file as FileUpload).fileType}
                      isEditing={canEditCustomers}
                      setEntityToBeDeleted={
                        canEditCustomers
                          ? this.props.onSetEntityToBeDeleted
                          : undefined
                      }
                      toggleDeleteConfirmationModal={
                        canEditCustomers
                          ? this.props.onDeleteCustomerInfoFile
                          : undefined
                      }
                    />
                  </Col>
                ))}
              </Row>
            </Panel>
          )}
        </Collapse>
      </div>
    );
  }
}
